import { useContext, useEffect, useState } from 'react'
import { getPerformanceSummary, getRecentMessages, getSlowestAnswers } from '../../api/api'
import { DashboardFilters } from '../../api/models'
import { AppStateContext, PerformanceData } from '../../state/AppProvider'

export type PerformanceDataState =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'loaded'; data: PerformanceData }

type FetchFailure = {
  filters: DashboardFilters
  message: string
}

const SLOWEST_ANSWER_LIMIT = 10
const RECENT_MESSAGE_LIMIT = 10
const NO_FILTERS: DashboardFilters = {}

export const usePerformanceData = (): PerformanceDataState => {
  const appStateContext = useContext(AppStateContext)
  const dispatch = appStateContext?.dispatch
  const filters = appStateContext?.state.filters ?? NO_FILTERS
  const cachedData =
    appStateContext?.state.performanceFilters === filters ? appStateContext.state.performanceData : null
  const [failure, setFailure] = useState<FetchFailure | null>(null)

  useEffect(() => {
    if (cachedData || !dispatch) return
    const controller = new AbortController()

    Promise.all([
      getPerformanceSummary(filters, controller.signal),
      getSlowestAnswers(filters, SLOWEST_ANSWER_LIMIT, controller.signal),
      getRecentMessages(filters, RECENT_MESSAGE_LIMIT, controller.signal),
    ])
      .then(([summary, slowest, recent]) => {
        if (controller.signal.aborted) return
        dispatch({
          type: 'SET_PERFORMANCE_DATA',
          payload: {
            data: {
              telemetryAvailable:
                summary.telemetry_available && slowest.telemetry_available && recent.telemetry_available,
              summary,
              slowest: slowest.items,
              slowestCostBasis: { currency: slowest.currency, is_estimate: slowest.is_estimate },
              recentMessages: recent.items,
              recentMessagesCostBasis: { currency: recent.currency, is_estimate: recent.is_estimate },
            },
            filters,
          },
        })
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return
        setFailure({ filters, message: err instanceof Error ? err.message : 'Failed to load performance data' })
      })

    return () => controller.abort()
  }, [filters, cachedData, dispatch])

  if (cachedData) return { status: 'loaded', data: cachedData }
  if (failure?.filters === filters) return { status: 'error', message: failure.message }
  return { status: 'loading' }
}
